import type { AchievementId, AchievementCategory, GamificationState, Goal } from '@/types'

// Achievement check result
export interface AchievementCheckResult {
  achievementId: AchievementId
  shouldUnlock: boolean
  newProgress: number
}

// Check all achievements that might be triggered by creating a goal
export function checkGoalCreationAchievements(
  goals: Goal[],
  newGoal: Goal
): AchievementCheckResult[] {
  const results: AchievementCheckResult[] = []
  const totalGoals = goals.length

  // First Steps - Create your first goal
  if (totalGoals === 1) {
    results.push({
      achievementId: 'first_steps',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  // Dream Big - Create a goal over $50,000
  if (newGoal.targetAmount > 50000) {
    results.push({
      achievementId: 'dream_big',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  // Planner Pro - Have 5 active goals simultaneously
  const activeGoals = goals.filter(g => g.status === 'active').length
  if (activeGoals >= 5) {
    results.push({
      achievementId: 'planner_pro',
      shouldUnlock: true,
      newProgress: activeGoals,
    })
  }

  // Data Driven - Link a goal to an account
  if (newGoal.linkedAccountId) {
    results.push({
      achievementId: 'data_driven',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  return results
}

// Check all achievements that might be triggered by completing a goal
export function checkGoalCompletionAchievements(
  goals: Goal[],
  completedGoal: Goal
): AchievementCheckResult[] {
  const results: AchievementCheckResult[] = []
  const completedGoals = goals.filter(g => g.status === 'completed').length

  // Goal Getter - Complete 1 goal
  results.push({
    achievementId: 'goal_getter',
    shouldUnlock: completedGoals >= 1,
    newProgress: completedGoals,
  })

  // Triple Threat - Complete 3 goals
  results.push({
    achievementId: 'triple_threat',
    shouldUnlock: completedGoals >= 3,
    newProgress: completedGoals,
  })

  // Goal Master - Complete 10 goals
  results.push({
    achievementId: 'goal_master',
    shouldUnlock: completedGoals >= 10,
    newProgress: completedGoals,
  })

  // Milestone achievements based on goal type
  if (completedGoal.type === 'emergency_fund') {
    results.push({
      achievementId: 'emergency_ready',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  if (completedGoal.type === 'debt_payoff') {
    results.push({
      achievementId: 'debt_destroyer',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  if (completedGoal.type === 'down_payment') {
    results.push({
      achievementId: 'homeward_bound',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  // Early Bird - Complete a goal before deadline
  if (completedGoal.deadline && completedGoal.completedAt) {
    const deadline = new Date(completedGoal.deadline)
    const completed = new Date(completedGoal.completedAt)
    if (completed < deadline) {
      results.push({
        achievementId: 'early_bird',
        shouldUnlock: true,
        newProgress: 1,
      })
    }
  }

  return results
}

// Check all achievements that might be triggered by making a contribution
export function checkContributionAchievements(
  totalSaved: number,
  isFirstContribution: boolean
): AchievementCheckResult[] {
  const results: AchievementCheckResult[] = []

  // First Dollar - Make your first contribution
  if (isFirstContribution) {
    results.push({
      achievementId: 'first_dollar',
      shouldUnlock: true,
      newProgress: 1,
    })
  }

  // Century Club - Save $100 toward goals
  results.push({
    achievementId: 'century_club',
    shouldUnlock: totalSaved >= 100,
    newProgress: Math.min(totalSaved, 100),
  })

  // Thousand Strong - Save $1,000 toward goals
  results.push({
    achievementId: 'thousand_strong',
    shouldUnlock: totalSaved >= 1000,
    newProgress: Math.min(totalSaved, 1000),
  })

  // Five Figure Saver - Save $10,000 toward goals
  results.push({
    achievementId: 'five_figure_saver',
    shouldUnlock: totalSaved >= 10000,
    newProgress: Math.min(totalSaved, 10000),
  })

  // Six Figure Club - Save $100,000 toward goals
  results.push({
    achievementId: 'six_figure_club',
    shouldUnlock: totalSaved >= 100000,
    newProgress: Math.min(totalSaved, 100000),
  })

  return results
}

// Get unlocked achievements count by category
export function getAchievementsByCategory(
  achievements: GamificationState['achievements']
): Record<AchievementCategory, { unlocked: number; total: number }> {
  const result: Record<AchievementCategory, { unlocked: number; total: number }> = {
    goals: { unlocked: 0, total: 0 },
    savings: { unlocked: 0, total: 0 },
    streaks: { unlocked: 0, total: 0 },
    milestones: { unlocked: 0, total: 0 },
    engagement: { unlocked: 0, total: 0 },
  }

  Object.values(achievements).forEach(achievement => {
    result[achievement.category].total += 1
    if (achievement.unlockedAt) {
      result[achievement.category].unlocked += 1
    }
  })

  return result
}

// Get total achievement stats
export function getAchievementStats(achievements: GamificationState['achievements']): {
  total: number
  unlocked: number
  totalPoints: number
  earnedPoints: number
} {
  const achievementList = Object.values(achievements)
  
  return {
    total: achievementList.length,
    unlocked: achievementList.filter(a => a.unlockedAt).length,
    totalPoints: achievementList.reduce((sum, a) => sum + a.points, 0),
    earnedPoints: achievementList.filter(a => a.unlockedAt).reduce((sum, a) => sum + a.points, 0),
  }
}

// Calculate goal progress percentage
export function calculateGoalProgress(goal: Goal): number {
  if (goal.targetAmount <= 0) return 0
  return Math.min(100, (goal.currentAmount / goal.targetAmount) * 100)
}

// Check if a goal is on track (based on deadline)
export function isGoalOnTrack(goal: Goal): boolean | null {
  if (!goal.deadline || goal.status !== 'active') return null
  
  const now = new Date()
  const deadline = new Date(goal.deadline)
  const created = new Date(goal.createdAt)
  
  // Calculate expected progress based on time elapsed
  const totalDuration = deadline.getTime() - created.getTime()
  const elapsed = now.getTime() - created.getTime()
  
  if (elapsed <= 0 || totalDuration <= 0) return true
  
  const expectedProgress = (elapsed / totalDuration) * 100
  const actualProgress = calculateGoalProgress(goal)
  
  // On track if actual progress >= 80% of expected progress
  return actualProgress >= expectedProgress * 0.8
}

// Get goals that are close to completion (>= 80%)
export function getGoalsNearCompletion(goals: Goal[]): Goal[] {
  return goals.filter(g => 
    g.status === 'active' && 
    calculateGoalProgress(g) >= 80 && 
    calculateGoalProgress(g) < 100
  )
}

// Get goals at risk (deadline approaching but not on track)
export function getGoalsAtRisk(goals: Goal[]): Goal[] {
  return goals.filter(g => {
    if (g.status !== 'active' || !g.deadline) return false
    
    const deadline = new Date(g.deadline)
    const now = new Date()
    const daysUntilDeadline = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    
    // At risk if less than 30 days and not on track
    return daysUntilDeadline <= 30 && daysUntilDeadline > 0 && !isGoalOnTrack(g)
  })
}

// Format streak display
export function formatStreak(days: number): string {
  if (days === 0) return 'No streak'
  if (days === 1) return '1 day'
  if (days < 7) return `${days} days`
  if (days < 30) return `${Math.floor(days / 7)} week${days >= 14 ? 's' : ''}`
  if (days < 365) return `${Math.floor(days / 30)} month${days >= 60 ? 's' : ''}`
  return `${Math.floor(days / 365)} year${days >= 730 ? 's' : ''}`
}

// Pages to track for explorer achievement
export const TRACKED_PAGES = [
  'dashboard',
  'budget',
  'portfolio',
  'debt',
  'real-estate',
  'retirement',
  'goals',
  'achievements',
  'education',
  'about',
]
